import { useState } from "react";
import type { BoardSnapshot } from "@kaba/shared";
import { api } from "../../lib/api";
import { boardKey, cardKey, queryClient } from "../../lib/board";
import { refreshLibrary, type LibraryScope } from "../../lib/library";
import { toast } from "../../lib/toast";
import { cx, Sheet } from "../ui/ui";
import { LibraryBrowser } from "./LibraryBrowser";

/**
 * „Aus Bibliothek“ in einer Karte: Datei wählen, optional als Titelbild – ohne erneute Übertragung.
 * 0.57: Bei Boards eines Teams wahlweise aus der Team-Bibliothek (nur freigegebene Ordner).
 */
export function LibraryPickDialog({
  open,
  boardId,
  cardId,
  onClose,
  cover = false,
  onPickFile,
}: {
  /** 0.89: Datei nur auswählen (z. B. als wichtiger Bezug des Boards) statt an eine Karte zu hängen */
  onPickFile?: (file: import("@kaba/shared").LibraryFile) => void;
  /** 0.74: aus dem Titelbild-Bereich – nur Bilder, direkt als Titelbild */
  cover?: boolean;
  open: boolean;
  boardId: string;
  cardId?: string;
  onClose: () => void;
}) {
  const teamId =
    queryClient.getQueryData<BoardSnapshot>(boardKey(boardId))?.board.teamId ??
    null;
  const [source, setSource] = useState<"board" | "team">("board");
  const scope: LibraryScope =
    source === "team" && teamId
      ? { kind: "boardTeam", boardId }
      : { kind: "board", boardId };
  return (
    <Sheet
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={cover ? "Titelbild aus Bibliothek" : onPickFile ? "Datei aus der Bibliothek wählen" : "Aus Bibliothek hinzufügen"}
      side="wide"
    >
      <div className="flex flex-col gap-3 p-4">
        {teamId && (
          <div
            role="group"
            aria-label="Bibliothek"
            className="flex gap-1 self-start rounded-lg bg-black/5 p-0.5 dark:bg-white/10"
          >
            {(
              [
                ["board", "Board"],
                ["team", "Team"],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                aria-pressed={source === k}
                onClick={() => setSource(k)}
                className={cx(
                  "min-h-11 rounded-md px-4 text-sm sm:min-h-9",
                  source === k
                    ? "bg-[var(--panel)] font-semibold shadow-sm"
                    : "text-[var(--ink-soft)]",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {open && (
          <LibraryBrowser
            key={scope.kind}
            scope={scope}
            teamId={teamId}
            canEdit
            mode="pick"
            pickCover={cover}
            pickLabel={onPickFile ? "Auswählen" : undefined}
            onPick={async ({ file, asCover }) => {
              if (onPickFile || !cardId) {
                onPickFile?.(file);
                onClose();
                return;
              }
              try {
                await api.post(
                  `/api/cards/${cardId}/attachments/from-library`,
                  { fileId: file.id, asCover },
                );
                await Promise.all([
                  queryClient.invalidateQueries({ queryKey: cardKey(cardId) }),
                  refreshLibrary(boardId),
                ]);
                toast.info(
                  cover
                    ? `„${file.name}“ ist jetzt das Titelbild.`
                    : `„${file.name}“ hinzugefügt${asCover ? " – als Titelbild" : ""}.`,
                );
                onClose();
              } catch (err) {
                toast.error((err as Error).message);
              }
            }}
          />
        )}
      </div>
    </Sheet>
  );
}
